import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import AppButton from "../button/AppButton";
import AppSelectInput from "../inputs/AppSelectInput";
import AppTextInput from "../inputs/AppTextInput";
import { AdminAccountResponse } from "../../models/ResponseModels/AdminAccountResponse";
import { AdminSubscriptionResponse } from "../../models/ResponseModels/AdminSubscriptionResponse";
import { UpdateAdminSubscriptionRequest } from "../../models/RequestModels/UpdateAdminSubscriptionRequest";
import { useAppSelector } from "../../store/hooks";

interface UpdateSubscriptionModalProps {
  open: boolean;
  account: AdminAccountResponse | null;
  subscriptions: AdminSubscriptionResponse[];
  onClose: () => void;
  onSubmit: (body: UpdateAdminSubscriptionRequest) => void;
}

const getSubscriptionLabel = (subscription: AdminSubscriptionResponse) => {
  const limit =
    subscription.property_limit === null
      ? "Unlimited properties"
      : `${subscription.property_limit} properties`;

  const visibility = subscription.is_public ? "public" : "admin only";

  return `${subscription.name} - ${limit} - ${visibility}`;
};

const UpdateSubscriptionModal = ({
  open,
  account,
  subscriptions,
  onClose,
  onSubmit,
}: UpdateSubscriptionModalProps) => {
  const { saving, fieldErrors } = useAppSelector(
    (state) => state.adminAccounts,
  );

  const defaultSubscriptionId = useMemo(() => {
    return account?.subscription_id || subscriptions[0]?.id || 0;
  }, [account?.subscription_id, subscriptions]);

  const [form, setForm] = useState<UpdateAdminSubscriptionRequest>({
    subscription_id: defaultSubscriptionId,
    subscription_status: "trial",
    duration_type: "months",
    duration: 1,
    notes: "",
    custom_unit_limit: 21,
    custom_unit_price_amount: 100,
    custom_unit_price_currency: "EUR",
  });

  const subscriptionOptions = useMemo(
    () =>
      subscriptions.map((subscription) => ({
        label: getSubscriptionLabel(subscription),
        value: String(subscription.id),
      })),
    [subscriptions],
  );

  const selectedSubscription = subscriptions.find(
    (subscription) => subscription.id === Number(form.subscription_id),
  );

  const isCustomSubscription = selectedSubscription?.code === "custom";

  useEffect(() => {
    if (!account) {
      return;
    }

    setForm({
      subscription_id: account.subscription_id || defaultSubscriptionId,
      subscription_status:
        account.subscription_status === "active" ||
        account.subscription_status === "expired" ||
        account.subscription_status === "cancelled"
          ? account.subscription_status
          : "trial",
      duration_type: "months",
      duration: 1,
      notes: account.notes || "",
      custom_unit_limit: account.custom_unit_limit || 21,

      custom_unit_price_amount: account.custom_unit_price_amount || 100,

      custom_unit_price_currency: "EUR",
    });
  }, [account, defaultSubscriptionId]);

  const updateField = <K extends keyof UpdateAdminSubscriptionRequest>(
    key: K,
    value: UpdateAdminSubscriptionRequest[K],
  ) => {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  };

  const getError = (key: string) => fieldErrors[key]?.[0];

  const shouldShowDuration =
    form.subscription_status === "trial" ||
    form.subscription_status === "active";

  const handleSubmit = () => {
    const body: UpdateAdminSubscriptionRequest = {
      ...form,

      subscription_id: Number(form.subscription_id),

      duration:
        form.duration === undefined || form.duration === null
          ? undefined
          : Number(form.duration),

      duration_type: shouldShowDuration
        ? form.duration_type || "months"
        : undefined,
    };

    if (!isCustomSubscription) {
      delete body.custom_unit_limit;
      delete body.custom_unit_price_amount;
      delete body.custom_unit_price_currency;
    }

    onSubmit(body);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Update subscription</DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <AppSelectInput
            label="Subscription"
            value={String(form.subscription_id || "")}
            onChange={(value) => updateField("subscription_id", Number(value))}
            error={getError("subscription_id")}
            disabled={subscriptions.length === 0}
            options={subscriptionOptions}
          />

          <AppSelectInput
            label="Status"
            value={form.subscription_status}
            onChange={(value) =>
              updateField(
                "subscription_status",
                value as UpdateAdminSubscriptionRequest["subscription_status"],
              )
            }
            error={getError("subscription_status")}
            options={[
              { label: "Trial", value: "trial" },
              { label: "Paid", value: "active" },
              { label: "Expired", value: "expired" },
              { label: "Cancelled", value: "cancelled" },
            ]}
          />

          {shouldShowDuration ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, 1fr)",
                },
                gap: 2,
              }}
            >
              <AppSelectInput
                label="Duration type"
                value={form.duration_type || "months"}
                onChange={(value) =>
                  updateField(
                    "duration_type",
                    value as NonNullable<
                      UpdateAdminSubscriptionRequest["duration_type"]
                    >,
                  )
                }
                error={getError("duration_type")}
                options={[
                  { label: "Days", value: "days" },
                  { label: "Months", value: "months" },
                  { label: "Years", value: "years" },
                ]}
              />

              <AppTextInput
                label="Duration"
                type="number"
                value={form.duration || 1}
                onChange={(value) => updateField("duration", Number(value))}
                error={getError("duration")}
              />
            </Box>
          ) : null}

          {isCustomSubscription ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, 1fr)",
                },
                gap: 2,
              }}
            >
              <AppTextInput
                label="Contracted units"
                type="number"
                value={form.custom_unit_limit || 21}
                onChange={(value) =>
                  updateField("custom_unit_limit", Number(value))
                }
                error={getError("custom_unit_limit")}
              />

              <AppTextInput
                label="Monthly price per unit (€)"
                type="number"
                value={(form.custom_unit_price_amount || 100) / 100}
                onChange={(value) =>
                  updateField(
                    "custom_unit_price_amount",
                    Math.round(Number(value) * 100),
                  )
                }
                error={getError("custom_unit_price_amount")}
              />
            </Box>
          ) : null}

          <AppTextInput
            label="Notes"
            value={form.notes || ""}
            onChange={(value) => updateField("notes", value)}
            error={getError("notes")}
            multiline
            rows={3}
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <AppButton variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </AppButton>

        <AppButton
          onClick={handleSubmit}
          loading={saving}
          disabled={subscriptions.length === 0}
        >
          Save changes
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateSubscriptionModal;
